import React from 'react';
import { infoPageStyles as styles } from './sharedStyles';

const FAQ = () => {
  const faqs = [
    {
      category: 'Getting Started',
      questions: [
        {
          q: 'Who can use RedBird Markets?',
          a: 'RedBird Markets is exclusively for Illinois State University students, faculty, and staff with a valid ISU email address.'
        },
        {
          q: 'Is RedBird Markets free to use?',
          a: 'Yes! Creating an account, browsing listings, and messaging other users is completely free. We only charge a small processing fee on completed transactions through our platform.'
        },
        {
          q: 'How do I verify my ISU email?',
          a: 'After signing up, you\'ll receive a verification email to your ISU email address. Click the link in the email to verify your account and access all features.'
        }
      ]
    },
    {
      category: 'Buying',
      questions: [
        {
          q: 'How do I search for items?',
          a: 'Use the search bar at the top of the marketplace, or browse by category. You can filter results by various methods.'
        },
        {
          q: 'How do I contact a seller?',
          a: 'Click on any listing to view details, then use the "Message Seller" button to start a conversation through our secure messaging system.'
        },
        {
          q: 'What payment methods are accepted?',
          a: 'We support secure platform payments through Stripe. Always use secure payment methods.'
        },
        {
          q: 'What if the item isn\'t as described?',
          a: 'Always inspect items before completing payment. If there\'s an issue, contact the seller immediately through our messaging system. You can also report problems through our dispute resolution process.'
        }
      ]
    },
    {
      category: 'Selling',
      questions: [
        {
          q: 'How do I create a listing?',
          a: 'Click "Sell" in the navigation menu, then fill out the listing form with photos, description, price, and category. Your listing will be live immediately after submission.'
        },
        {
          q: 'How many photos can I upload?',
          a: 'You can upload up to 10 photos per listing. We recommend using multiple angles and clear, well-lit images.'
        },
        {
          q: 'Can I edit my listing after posting?',
          a: 'Yes! Go to "My Items" in your dashboard, find your listing, and click "Edit" to make changes at any time.'
        },
        {
          q: 'How do I mark an item as sold?',
          a: 'In your "My Items" section, click on the listing and select "Mark as Sold". This will remove it from the marketplace and add it to your sales history.'
        },
        {
          q: 'What happens to my listing after it sells?',
          a: 'When an item is sold through our platform, it\'s automatically marked as sold and removed from the marketplace. You can still contact the buyer and view sale details from your "My Items" page.'
        },
        {
          q: 'Can I archive old listings?',
          a: 'Yes! You can archive completed sales to keep your dashboard organized. Archived items are hidden from your main view but your transaction history is always preserved. Listings archived for 90+ days are automatically moved to deep storage.'
        },
        {
          q: 'What items can I sell?',
          a: 'You can sell most items including textbooks, furniture, electronics, clothing, and more. Check our Community Guidelines for prohibited items.'
        },
        {
          q: 'How do I get paid when something sells?',
          a: 'Payments are handled automatically by Stripe. When someone buys your item, Stripe deposits the money directly into your bank account within 1-2 business days. No manual withdrawal needed - it\'s completely automatic!'
        },
        {
          q: 'Do I need to withdraw my earnings manually?',
          a: 'No! Stripe automatically deposits your earnings directly to your bank account daily. You don\'t need to withdraw anything - just add your bank details during seller setup and payments arrive automatically.'
        },
        {
          q: 'What fees do I pay as a seller?',
          a: 'Sellers pay a 5% platform fee plus Stripe\'s processing fees (2.9% + $0.30). These are automatically deducted from your earnings before deposit. No monthly fees or hidden charges.'
        }
      ]
    },
    {
      category: 'Safety & Security',
      questions: [
        {
          q: 'Is RedBird Markets safe?',
          a: 'Yes! We\'ve implemented multiple safety features including ISU email verification, secure messaging, and payment protection through Stripe. Always follow our safety guidelines.'
        },
        {
          q: 'Where should I meet for transactions?',
          a: 'Always meet in public, well-lit areas on campus such as Bone Student Center, Milner Library, or other busy campus locations. Never meet in private residences.'
        },
        {
          q: 'How do I report a user or issue?',
          a: 'You can report a user or issue by contacting us directly through the Help Center.'
        },
        {
          q: 'Are my personal details safe?',
          a: 'Yes. We never share your personal information publicly. Keep communications within the platform and don\'t share sensitive details in messages.'
        },
        {
          q: 'How long do you keep my transaction history?',
          a: 'Your transaction records are preserved indefinitely to maintain payment history and buyer-seller communication access. Even if you archive or delete listings, the transaction data remains safe for legal and operational purposes.'
        }
      ]
    },
    {
      category: 'Account & Settings',
      questions: [
        {
          q: 'How do I change my profile information?',
          a: 'Go to your Profile page and click "Edit Profile". You can update your name, photo, and bio.'
        },
        {
          q: 'I forgot my password. What should I do?',
          a: 'Click "Forgot Password" on the login page and follow the instructions sent to your ISU email.'
        }
      ]
    },
    {
      category: 'Technical Issues',
      questions: [
        {
          q: 'The website isn\'t loading properly. What should I do?',
          a: 'Try clearing your browser cache and cookies, or try a different browser. If the problem persists, contact our support team.'
        },
        {
          q: 'I can\'t upload photos. Help!',
          a: 'Make sure your images are under 5MB each and in JPG, PNG, or WEBP format. Try resizing large images before uploading.'
        },
        {
          q: 'My messages aren\'t sending.',
          a: 'Check your internet connection and try refreshing the page. If the issue continues, contact support.'
        }
      ]
    }
  ];

  return (
    <div style={styles.pageContainer}>
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Frequently Asked Questions</h1>
        <p style={styles.headerSubtitle}>
          Find answers to common questions about RedBird Markets
        </p>
      </div>

      <div style={styles.contentContainer}>
        <div style={styles.section}>
          <p style={styles.text}>
            Can't find what you're looking for? Check our <a href="/help-center" style={styles.link}>Help Center</a> or <a href="/contact-us" style={styles.link}>Contact Us</a> directly.
          </p>
        </div>

        {faqs.map((category, idx) => (
          <div key={idx} style={styles.section}>
            <h2 style={styles.sectionTitle}>{category.category}</h2>
            
            {category.questions.map((faq, qIdx) => (
              <div key={qIdx} style={{marginBottom: '24px'}}>
                <h3 style={styles.subsectionTitle}>{faq.q}</h3>
                <p style={styles.text}>{faq.a}</p>
                {qIdx < category.questions.length - 1 && <hr style={styles.divider} />}
              </div>
            ))}
          </div>
        ))}

        {/* Still Have Questions */}
        <div style={{...styles.section, textAlign: 'center'}}>
          <h2 style={styles.sectionTitle}>Still Have Questions?</h2>
          <p style={styles.text}>
            Our support team is here to help. Reach out through our Help Center or send us a message.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
